import React from 'react';
import Text from '../../components/Text.js';
import Bubble from '../../components/Bubble.js';
import TextInput from '../../components/TextFields/TextInput.js';
import DateInput from '../../components/TextFields/DateInput.js';
import BubbleSelectBlock from '../../components/BubbleSelectBlock.js';
import Grid from '@mui/material/Grid';
import * as Services from '../../services/index.js';
import i18n from '../../localization/i18n.js';
import tokens from '../../localization/tokens.js';
import * as localizationMaps from '../../localization/maps.js';
import fonts from '../../theme/fonts.js';
import colors from '../../theme/colors.js';
import * as dates from "@simplybenefits/sb-dates";

/**
 * 
 * @param {*} value 
 * @returns 
 */
function isDefined(value) {
  return value !== undefined && value !== null;
}

/**
 * Ensures on edit that old details have at least one item to display (other than dependentID)
 * @param {*} obj 
 * @returns {boolean}
 */
function hasDataToDisplay(obj) {
  return Object.keys(obj).some(key => key !== 'dependentID' && obj[key] !== null);
}

/**
 * Generic info regarding dependents
 * @param data
 * @param handleChange
 * @param isDisabled
 * @param useNewData
 * @returns 
 */
function DependentDetails({
  data,
  errors,
  handleChange,
  isDisabled,
  useNewData = true
}) {
  // select old or new data to display
  const sectionData = data[useNewData ? 'newData' : 'oldData'];
  return /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    columnSpacing: 2
  }, isDefined(sectionData.firstname) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.firstname),
    value: sectionData.firstname,
    onChange: event => handleChange('firstname', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.firstname,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.lastname) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.lastname),
    value: sectionData.lastname,
    onChange: event => handleChange('lastname', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.lastname,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.gender) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.gender),
    value: localizationMaps.fromEnglish('genders', sectionData.gender || ''),
    onChange: event => handleChange('gender', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.gender,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled,
    select: true,
    selectOptions: Services.selectFieldOptions.genderOptions()
  })), isDefined(sectionData.birthdate) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.birthdate),
    value: sectionData.birthdate,
    onChange: event => handleChange('birthdate', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.birthdate,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.email) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.changeRequests.emailOptional),
    value: sectionData.email,
    onChange: event => handleChange('email', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.email,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.relationship) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.relationship),
    value: localizationMaps.fromEnglish('relationships', sectionData.relationship || ''),
    onChange: async event => {
      handleChange('relationship', event.target.value, useNewData ? 'newData' : 'oldData');
      if (event.target.value === 'Common Law') {
        await handleChange('cohabDate', sectionData.cohabDate || dates.formatIncomingDate(), useNewData ? 'newData' : 'oldData');
      }
      if (event.target.value === 'Spouse') {
        await handleChange('dateOfMarriage', sectionData.dateOfMarriage || dates.formatIncomingDate(), useNewData ? 'newData' : 'oldData');
      }
    },
    error: !isDisabled && errors.relationship,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled,
    select: true,
    selectOptions: Services.selectFieldOptions.dependentOptionsWithSpouse()
  })), isDefined(sectionData.cohabDate) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.changeRequests.coHabDate),
    value: sectionData.cohabDate,
    onChange: event => handleChange('cohabDate', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.cohabDate,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })), isDefined(sectionData.dateOfMarriage) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 4
  }, /*#__PURE__*/React.createElement(DateInput, {
    label: i18n.t(tokens.labels.changeRequests.marriageDate),
    value: sectionData.dateOfMarriage,
    onChange: event => handleChange('dateOfMarriage', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.dateOfMarriage,
    variant: "outlined",
    fullWidth: true,
    disabled: isDisabled
  })));
}

/**
 * Sub questions regarding dependent type
 * @param data
 * @param handleChange
 * @param isDisabled
 * @param useNewData
 * @returns 
 */
function DependentSubSection({
  data,
  errors,
  handleChange,
  isDisabled,
  useNewData = true
}) {
  // select old or new data to use
  const sectionData = data[useNewData ? 'newData' : 'oldData'];
  const benefitOptions = ["Major Medical", "Vision", "Paramedical", "Drugs", "Dental", "Out of Country & Province"];

  /**
   * Helper for toggling the spousal benefit bubbles
   * @param benefit 
   */
  async function _handleToggleBenefit(benefit) {
    const currentBenefits = sectionData.primaryBenefits || [];
    const updatedBenefits = currentBenefits.includes(benefit) ? currentBenefits.filter(item => item !== benefit) : [...currentBenefits, benefit];
    await handleChange('primaryBenefits', updatedBenefits, useNewData ? 'newData' : 'oldData');
  }
  const styles = {
    benefits: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '8px',
      marginBottom: '10px',
      marginTop: '10px'
    }
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, isDefined(sectionData.hasProvincialHealthCoverage) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    light: true,
    small: true
  }, i18n.t(tokens.labels.dependents.doTheyHaveProvincialHealthCoverage)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    first: {
      title: `${i18n.t(tokens.buttons.yes)}`,
      selected: Boolean(sectionData.hasProvincialHealthCoverage) === true,
      field: 'hasProvincialHealthCoverageYes'
    },
    second: {
      title: `${i18n.t(tokens.buttons.no)}`,
      selected: Boolean(sectionData.hasProvincialHealthCoverage) === false,
      field: 'hasProvincialHealthCoverageNo'
    },
    disabled: isDisabled,
    handleChange: async field => {
      if (field === 'hasProvincialHealthCoverageYes') {
        await handleChange('hasProvincialHealthCoverage', true, useNewData ? 'newData' : 'oldData');
      } else {
        await handleChange('hasProvincialHealthCoverage', false, useNewData ? 'newData' : 'oldData');
      }
    }
  })), isDefined(sectionData.spouseCoverage) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    light: true,
    small: true
  }, i18n.t(tokens.labels.changeRequests.separateCoverage)), /*#__PURE__*/React.createElement(BubbleSelectBlock, {
    first: {
      title: `${i18n.t(tokens.buttons.yes)}`,
      selected: Boolean(sectionData.spouseCoverage) === true,
      field: 'spouseCoverageYes'
    },
    second: {
      title: `${i18n.t(tokens.buttons.no)}`,
      selected: Boolean(sectionData.spouseCoverage) === false,
      field: 'spouseCoverageNo'
    },
    disabled: isDisabled,
    handleChange: async field => {
      if (field === 'spouseCoverageYes') {
        await handleChange('spouseCoverage', true, useNewData ? 'newData' : 'oldData');
      } else {
        await handleChange('spouseCoverage', false, useNewData ? 'newData' : 'oldData');
      }
    }
  })), /*#__PURE__*/React.createElement(Grid, {
    container: true,
    direction: "row",
    columnSpacing: 2
  }, isDefined(sectionData.employer) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.changeRequests.dependentOrganization),
    value: sectionData.employer,
    onChange: event => handleChange('employer', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.employer,
    disabled: isDisabled,
    variant: "outlined",
    fullWidth: true
  })), isDefined(sectionData.insuranceCarrier) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.changeRequests.dependentCurrentInsuranceCarrier),
    value: sectionData.insuranceCarrier,
    onChange: event => handleChange('insuranceCarrier', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.insuranceCarrier,
    disabled: isDisabled,
    variant: "outlined",
    fullWidth: true
  })), isDefined(sectionData.dependentCertNumber) && /*#__PURE__*/React.createElement(Grid, {
    item: true,
    xs: 3
  }, /*#__PURE__*/React.createElement(TextInput, {
    label: i18n.t(tokens.labels.changeRequests.dependentCertificateNumber),
    value: sectionData.dependentCertNumber,
    onChange: event => handleChange('dependentCertNumber', event.target.value, useNewData ? 'newData' : 'oldData'),
    error: !isDisabled && errors.dependentCertNumber,
    disabled: isDisabled,
    variant: "outlined",
    fullWidth: true
  }))), isDefined(sectionData.primaryBenefits) && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    body: true,
    light: true
  }, i18n.t(tokens.labels.changeRequests.benefits)), /*#__PURE__*/React.createElement(Text, {
    light: true,
    small: true
  }, i18n.t(tokens.labels.changeRequests.pleaseIndicateBenefit)), /*#__PURE__*/React.createElement("div", {
    style: styles.benefits
  }, benefitOptions.map(benefit => /*#__PURE__*/React.createElement(Bubble, {
    key: benefit,
    label: benefit,
    isSelected: sectionData.primaryBenefits?.includes(benefit),
    onClick: () => _handleToggleBenefit(benefit),
    disabled: isDisabled,
    disabledHover: isDisabled,
    clickable: !isDisabled,
    customStyle: {
      padding: '0px',
      margin: '0px',
      fontFamily: fonts.type.heading,
      fontSize: 12,
      minWidth: "0px",
      color: colors.mainTextGrey
    }
  })))));
}

/**
 * @param data
 * @param errors
 * @param isMaster
 * @param handleChange
 * @returns 
 */
function DependentChange({
  data,
  errors,
  isMaster = false,
  handleChange
}) {
  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column'
    },
    sectionHeader: {
      marginTop: '25px'
    }
  };

  /**
   * Always disabled for admin/members, disabled for master users if not in 'Pending', 'Incomplete', or 'Suspended' status
   */
  const isDisabled = !isMaster || isMaster && !['PENDING AGENT REVIEW', 'INCOMPLETE', 'SUSPENDED'].includes(data.status?.toUpperCase());
  return /*#__PURE__*/React.createElement("div", {
    style: styles.container
  }, data.type === 'Add a Dependent' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true,
    customStyle: styles.sectionHeader
  }, i18n.t(tokens.labels.changeRequests.addDependent)), /*#__PURE__*/React.createElement(DependentDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: isDisabled,
    useNewData: true
  }), /*#__PURE__*/React.createElement(DependentSubSection, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: isDisabled,
    useNewData: true
  })), data.type === 'Edit Dependent Details' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true,
    customStyle: {
      ...styles.sectionHeader,
      marginBottom: '25px'
    }
  }, i18n.t(tokens.labels.changeRequests.editDependent)), hasDataToDisplay(data.oldData) && /*#__PURE__*/React.createElement("div", {
    style: {
      marginBottom: '25px'
    }
  }, /*#__PURE__*/React.createElement(Text, {
    body: true,
    light: true
  }, i18n.t(tokens.labels.changeRequests.oldDependentDetails)), /*#__PURE__*/React.createElement(DependentDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: true,
    useNewData: false
  }), /*#__PURE__*/React.createElement(DependentSubSection, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: true,
    useNewData: false
  })), /*#__PURE__*/React.createElement(Text, {
    body: true,
    light: true
  }, i18n.t(tokens.labels.changeRequests.newDependentDetails)), /*#__PURE__*/React.createElement(DependentDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: isDisabled,
    useNewData: true
  }), /*#__PURE__*/React.createElement(DependentSubSection, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: isDisabled,
    useNewData: true
  })), data.type === 'Remove a Dependent' && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    bold: true,
    medium: true,
    customStyle: styles.sectionHeader
  }, i18n.t(tokens.labels.changeRequests.removeDependent)), /*#__PURE__*/React.createElement(DependentDetails, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: true,
    useNewData: false
  }), /*#__PURE__*/React.createElement(DependentSubSection, {
    data: data,
    errors: errors,
    handleChange: handleChange,
    isDisabled: true,
    useNewData: false
  })));
}
export default DependentChange;